import { Collapse, Table, TableBody, TableCell, TableHead, TablePagination, TableRow } from "@material-ui/core";
import { inject, observer } from "mobx-react";
import React from "react";
import { useTranslation } from "react-i18next";
import { Heading, Paper, MultiSelect, Option, Button, Icon, Tooltip, Text, Avatar, Spacer } from "src/components";
import { ItemStores, LogStore, RoutingStore } from "src/stores";
import styled from "styled-components";
import { Row } from "../room/editData/gridElements";
import { NewsItemRow } from "../dashboard/newsItemRow";
import { getRoutePath, MatchParams, Page } from "src/routes";
import { routingStore } from "src/app";
import { Badge } from "src/components/badge";
import { theme } from "src/config";
import { Log } from "src/models";
import { Colors, ItemStatus, ItemType, LogType, RoomLogProps } from "src/types";
import { LogFilter } from "./logInputs";
import { EditItem } from "../room/editData";
import { LogText } from "src/views/log";
import Circle from "src/components/spinner";


export interface ILogsProps {
  itemStores?: ItemStores;
  logStore?: LogStore;
  params?: MatchParams;
  routingStore?: RoutingStore;
}

export const Logs = inject("logStore", "itemStores", "routingStore")(observer((props: ILogsProps) => {
  const { logStore, itemStores } = props;
  const { t, i18n } = useTranslation();

  const logOptions = [
    { value: "question", label: t("log_title", { context: "question" }) },
    { value: "deliverable", label: t("log_title", { context: "deliverable" }) },
    { value: "action", label: t("log_title", { context: "action" }) }
  ];
  const [log_type, setLogType] = React.useState<Option>(logOptions.find(o => o.value === props.params.type));
  const [showAdvFilter, setShowAdvFilter] = React.useState<boolean>(false);
  const [isGeneratingLog, setIsGeneratingLog] = React.useState<boolean>(false);

  const isActionLog = log_type.value === "action";
  React.useEffect(() => {
    logStore.loadRoomLogs(log_type.value);
    logStore.resetRoomLogFilter();

    routingStore.push(getRoutePath(Page.RoomLog, {
      id: Number(props.params.id),
      view: "log",
      type: log_type.value
    }));

  }, [log_type]);

  React.useEffect(() => {
    if (!showAdvFilter && !logStore.isEmptyRoomLogFilter) logStore.resetRoomLogFilter();
  }, [showAdvFilter]);

  const rows: { [key: string]: string | number | Log }[] = React.useMemo(() =>
    logStore.roomLog.getArray.map(l => l.getLogRow)
    , [log_type, logStore.roomLog.getArray]);

  return (
    <StyledPaper
      style={{ maxWidth: "115rem", marginLeft: '2rem', padding: 0 }}
      display="flex"
      flexDirection="column"
      width="100%"
      $noShadow
    >
      <Row style={{ padding: "2rem 2rem" }} justify="space-between" height="fit-content" width="100%">
        <Heading type="h3">{t("log_title", { context: log_type.value })}</Heading>
        <div style={{ display: "flex", gap: "1rem" }}>
          <MultiSelect
            options={logOptions}
            selected={[log_type]}
            onChange={setLogType}
            singleSelect
            forceSelect
          />
          {isGeneratingLog ?
            <SpinnerContainer>
              <Circle color={Colors.Primary} size={2} sizeUnit="rem" />
            </SpinnerContainer>
            :
            <>
              <Button
                size="large"
                variant="icon"
                onClick={async () => {
                  setIsGeneratingLog(true);
                  await logStore.generateRoomLogFileExport(log_type.value as LogType, 'xlsx');
                  setIsGeneratingLog(false);
                }}
              >
                <Icon size="xlarge" iconId="excel" />
              </Button>
              <Button
                size="large"
                variant="icon"
                onClick={async () => {
                  setIsGeneratingLog(true);
                  await logStore.generateRoomLogFileExport(log_type.value as LogType, 'pdf')
                  setIsGeneratingLog(false);
                }}
              >
                <Icon size="xlarge" iconId="pdf" />
              </Button>
            </>
          }
        </div>
      </Row>
      <OuterContainer>
        <StyledTable style={{ backgroundColor: "white" }} stickyHeader>
          {/* Table header */}
          <TableHead>
            <TableRow>
              {!isActionLog && <TableCell></TableCell>}
              {columns[log_type.value as LogType].map(c => (
                <TableCell key={c.label}>
                  <Text style={{ textTransform: "capitalize" }} size="default" weight="bold">
                    {t(c.label)}
                  </Text>
                </TableCell>
              ))}
              <TableCell>
                <Tooltip content={t("show_filter")}>
                  <Button variant="icon" onClick={() => setShowAdvFilter(!showAdvFilter)}>
                    <Icon size="large" iconId="filter" />
                  </Button>
                </Tooltip>
              </TableCell>
            </TableRow>
            {/* Search filter row */}

          </TableHead>
          <TableBody style={{ overflow: "auto", verticalAlign: "bottom" }}>
            {showAdvFilter &&
              <TableRow>
                {!isActionLog && <FilterCell></FilterCell>}
                {columns[log_type.value as LogType].map(c => (
                  <FilterCell key={c.label}>
                    {c.filter && <LogFilter prop={c.prop || c.label as RoomLogProps} type={c.filter} />}
                  </FilterCell>
                ))}
                <TableCell></TableCell>
              </TableRow>
            }
          </TableBody>

          {/* Main Item Rows */}
          {rows.map((r, i) => (
            <StyledTableBody key={"" + r.number}>
              <TableRow
                key={"" + r.number}
                onClick={() => Number(r.item_id) !== logStore.activeLogItem ?
                  logStore.setActiveLogItem(Number(r.item_id), Number(r.room_id))
                  : logStore.setActiveLogItem(null, null)
                }
                hover
              >
                {!isActionLog &&
                  <TableCell>
                    {Number(r.item_id) === logStore.activeLogItem ? <Icon size="medium" iconId="chevronUp" /> : <Icon size="medium" iconId="chevronDown" />}
                  </TableCell>
                }
                {columns[log_type.value as LogType].map(c => (
                  <TableCell key={r.number + c.label}>
                    <div style={{ display: "flex", flexDirection: "row", gap: 10, alignItems: 'center' }}>
                      {c.label === "user" &&
                        <Avatar
                          size="default"
                          image={"" + r.avatar}
                          initials={"" + r.getInitials}
                        />
                      }
                      <Text size="small" color={r[c.label] ? undefined : theme.color.slate11} weight={r[c.label] ? undefined : "thin"}>
                        {c.label === "action" ?
                          <LogText log={r.log as Log} />
                          : (r[c.prop] || r[c.label] || t("not_available"))
                        }
                      </Text>
                    </div>
                  </TableCell>
                ))}
                <TableCell>
                  {!isActionLog &&
                    <Tooltip placement="left" content={t("open_item", { item: log_type.value })}>
                      <Button variant="icon" onClick={(e) => { e.stopPropagation(), itemStores[log_type.value as ItemType].setActiveById(r.item_id as number) }}>
                        <Icon size="medium" iconId="room" />
                      </Button>
                    </Tooltip>
                  }
                </TableCell>
              </TableRow>
              {/* Collapsible log details */}
              <TableRow>
                <TableCell style={{ padding: 0, borderBottom: "none" }} colSpan={12}>
                  <Collapse in={!!logStore.logItemEntries.getTotal && Number(r.item_id) === logStore.activeLogItem} timeout={300} unmountOnExit>
                    <div style={{ marginTop: "0rem", display: "flex", flexDirection: "column", width: '100%' }}>
                      <NewsItemRow />
                      {(logStore.logItemEntries.getTotal > logStore.logItemEntries.getPage) && (
                        <CollapsePagination
                          page={logStore.logItemEntries.getPage}
                          pageSize={logStore.newsItemEntryPageSize}
                          total={logStore.logItemEntries.getTotal}
                          onPageChange={(page: number) => {
                            logStore.logItemEntries.goToPage(page);
                          }}
                        />
                      )}
                    </div>
                  </Collapse>
                </TableCell>

              </TableRow>
            </StyledTableBody>
          ))}

        </StyledTable>
      </OuterContainer>
      <div style={{ height: "fit-content" }}>
        <TablePagination
          component="div"
          labelDisplayedRows={({ from, to, count, page }) => t("pagination", { from, to, count, page })}
          rowsPerPageOptions={[]}
          rowsPerPage={logStore.roomLogPageSize}
          count={logStore.roomLog.getTotal}
          page={logStore.roomLog.getPage}
          onPageChange={(e, page: number) => logStore.roomLog.goToPage(page)}
        />
      </div>
      <EditItem />
    </StyledPaper>
  );
}));

/**
 * @type   Key of the local string for the display label
 * @filter Enable filter input when advanced filter is enabled
 * @prop   prop to be filtered by, defaults to label
 * @inputType If @filter is true, this determins the type of the input used to filter. Defaults to text input
 */
type LogColumn = {
  label: string,
  filter?: "select" | "text" | "number" | "date",
  prop?: RoomLogProps,
}

const questionColumns: LogColumn[] = [
  { label: "number", filter: "number" },
  { label: "from", filter: "text" },
  { label: "to", filter: "text" },
  { label: "title", filter: "text" },
  { label: "last_changed", filter: "date", prop: "created_at" },
  { label: "response_date", filter: "date", prop: "response_date" },
  { label: "status", filter: "select" },
  { label: "meetingroom" },
];
const delivColumns: LogColumn[] = [
  { label: "number", filter: "number" },
  { label: "to", filter: "text" },
  { label: "title", filter: "text" },
  { label: "last_changed", filter: "date", prop: "created_at" },
  { label: "start_date", filter: "date"},
  { label: "end_date", filter: "date", prop: "date" },
  { label: "delay_date", filter: "date", prop: 'delay_date' },
  { label: "status", filter: "select" },
  { label: "meetingroom" },
];
const actionColumns: LogColumn[] = [
  { label: "user", filter: "text" },
  { label: "created_at", filter: "date" },
  { label: "action", filter: "select" },
  { label: "meetingroom" },
];

const columns: Record<LogType, LogColumn[]> = {
  question: questionColumns,
  deliverable: delivColumns,
  action: actionColumns
};

const OuterContainer = styled.div`
  height:        80rem; 
  margin-bottom: 2rem;
  overflow:      auto;
`
const StyledTable = styled(Table)`
  .MuiTableCell-stickyHeader {
    background-color: white;
  }
`

const StyledPaper = styled(Paper)`
  padding:       0;
  max-width:     125rem;
  overflow:      auto;
  margin-top:    2.5vh;
  margin-bottom: 2.5vh;
`;


const FilterCell = styled(TableCell)`
  &.MuiTableCell-root {
    padding:     1rem;
    padding-top: 2.5rem;
  }
`;

type CollapsePaginationProps = {
  page?: number;
  total?: number;
  pageSize?: number;
  onPageChange: (page: number) => void;
}

const CollapsePagination = ({
  page,
  pageSize,
  onPageChange,
  total
}: CollapsePaginationProps) => {
  
  const { t } = useTranslation();

  const from = page * pageSize + 1;
  const to = Math.min((page + 1) * pageSize, total);

  return (
    <PaginationContainer>
      <PageCountContainer>
        <Text size="1rem" style={{ marginRight: "0.5rem" }}>
          {from} - {to}
        </Text>
        <Text size="1rem">
          {t("of")}
        </Text>
        <Text size="1rem" style={{ marginLeft: "0.5rem", marginRight: "2rem" }}>
          {total}
        </Text>
        <Button
          onClick={() => onPageChange(page - 1)}
          variant="icon"
          disabled={!page}
        >
          <Icon size="small" iconId="chevronLeft" />
        </Button>
        <Spacer size="xxsmall" />
        <Button
          onClick={() => onPageChange(page + 1)}
          variant="icon"
          disabled={(page + 1) * pageSize >= total}
        >
          <Icon size="small" iconId="chevronRight" />
        </Button>
      </PageCountContainer>
    </PaginationContainer>
  )
}

const StyledTableBody = styled(TableBody)(({ t }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: "white",
  },
  '&:nth-of-type(even)': {
    backgroundColor: `${theme.color.slate3}`,
  },
}));


const PageCountContainer = styled.div`
  display:         flex;
  flex-direction:  row;
  justify-content: flex-end;
  align-items:     center;
  width:           20rem;
`

const PaginationContainer = styled.div`
  display:         flex;
  flex-direction:  row;
  align-items:     start;
  justify-content: center;
`

const SpinnerContainer = styled.div`
  width:           4rem;
  height:          100%;
  display:         flex;
  justify-content: center;
  align-items:     center;
`;